/**
 * Error boundary for every screen + the lazy-screen loading state. A crash in one screen shows a friendly card
 * (the child's drawings are stored separately and are never touched) instead of a white page; it resets when the
 * route changes. A failed lazy chunk (an old tab after a deploy, flaky network) reloads the page once.
 */
import { Component, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useT } from '../lib/i18n';
import { Scribble } from './characters';

const RELOAD_KEY = 'sketchling-chunk-reload';
const isChunkError = (e: unknown) => /dynamically imported module|Importing a module script failed|Failed to fetch dynamically|ChunkLoadError|Loading chunk/i.test(String((e as Error)?.message ?? e));

function CrashCard({ onRetry }: { onRetry: () => void }) {
  const t = useT();
  return (
    <div className="screen-crash col center" role="alert" data-testid="screen-crash">
      <Scribble mood="curious" size={96} />
      <h2>{t('crashTitle')}</h2>
      <p className="muted">{t('crashBody')}</p>
      <div className="row" style={{ gap: 10, justifyContent: 'center' }}>
        <button type="button" className="btn btn-primary" onClick={onRetry}>
          {t('crashRetry')}
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => window.location.assign('/')}>
          {t('crashHome')}
        </button>
      </div>
    </div>
  );
}

class Boundary extends Component<{ children: ReactNode; resetKey: string }, { error: unknown }> {
  override state = { error: null as unknown };
  static getDerivedStateFromError(error: unknown) {
    return { error };
  }
  override componentDidCatch(error: unknown) {
    if (isChunkError(error)) {
      try {
        if (!sessionStorage.getItem(RELOAD_KEY)) {
          sessionStorage.setItem(RELOAD_KEY, '1');
          window.location.reload();
          return;
        }
      } catch {
        /* storage blocked: show the card */
      }
    }
    console.error('[screen crash]', error);
  }
  override componentDidUpdate(prev: { resetKey: string }) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }
  override render() {
    if (this.state.error) return <CrashCard onRetry={() => (isChunkError(this.state.error) ? window.location.reload() : this.setState({ error: null }))} />;
    return this.props.children;
  }
}

export function ScreenBoundary({ children }: { children: ReactNode }) {
  const loc = useLocation();
  // A page that ran fine for a while may reload again on the next broken chunk (a later deploy).
  useEffect(() => {
    const id = setTimeout(() => {
      try {
        sessionStorage.removeItem(RELOAD_KEY);
      } catch {
        /* ignore */
      }
    }, 8000);
    return () => clearTimeout(id);
  }, []);
  return <Boundary resetKey={loc.pathname}>{children}</Boundary>;
}

export function ScreenLoading() {
  const t = useT();
  return (
    <div className="screen-loading col center" aria-busy="true" data-testid="screen-loading">
      <Scribble mood="happy" size={64} className="screen-loading-mascot" />
      <small className="muted">{t('loadingScreen')}</small>
    </div>
  );
}
